<script>
// The light/dark switch from the admin header, shared so every page that offers a theme choice
// shows the same control. It only reports clicks: each page owns where the choice is stored and
// what it repaints, so the parent passes the current state in and handles `toggle`.
export default {
  name: "ThemeToggle",

  props: {
    dark: { type: Boolean, default: false },
  },
};
</script>

<template>
  <!-- A plain <button>, like ChooseLocale's trigger, rather than a v-btn. Vuetify's
       icon button forces a fixed square, its own background and a ripple overlay, all
       of which had to be fought with !important to look like the language control —
       using the same element as that control removes the fight.

       role="switch" rather than a bare button: the knob's position reports the
       current theme, so assistive tech needs the on/off state, not just a label.
       aria-checked is bound as a string because Vue drops an attribute whose
       value is boolean false, which would leave the switch stateless in light
       mode. The knob's own glyph follows the same rule as its position — it
       shows the theme you are in, not the one you would switch to. -->
  <button
    type="button"
    class="theme-toggle"
    :class="{ 'theme-toggle--dark': dark }"
    role="switch"
    :aria-checked="dark ? 'true' : 'false'"
    :aria-label="dark ? 'Switch to light mode' : 'Switch to dark mode'"
    @click="$emit('toggle')"
  >
    <span class="theme-toggle-knob">
      <!-- ?v=2 on the night icon is a cache bust, not decoration: nginx serves
           /icons/* with max-age=2592000 and these files are not content-hashed,
           so browsers that loaded the old solid crescent would keep it for 30
           days. The query lives in the hashed JS bundle, so it reaches everyone
           on the next deploy. Bump it again if the file's artwork changes.
           alt is empty because the button's aria-label already names the
           control — a filled alt here would announce it twice. -->
      <img
        :src="dark ? '/icons/night-icon.svg?v=2' : '/icons/day-icon.svg'"
        alt=""
        class="theme-toggle-icon"
      />
    </span>
  </button>
</template>

<style lang="scss" scoped>
/* ─── Theme Toggle ───
   A switch, not an icon button: a 56×32 capsule whose knob slides between the ends, so
   position reports the current theme instead of the pending action. The track keeps the
   material from the square version — a quiet translucent fill with the glass in the rim,
   drawn as a ring by masking the middle out of a pseudo-element — because that is the
   part the header already speaks. The knob stays light in both themes and the glyph on
   it stays dark, which is why there is only one icon filter now instead of two.
   Geometry: 3px inset all round, so a 26px knob travels 56 − 26 − (3 × 2) = 24px. */
.theme-toggle {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 56px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;

  background: rgba(10, 18, 34, 0.08);
  /* The saturation boost is the vibrancy — it is what stops a blurred backdrop from
     going flat and grey, and it is the part people actually read as "macOS". */
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  /* The track is a groove the knob sits in, so its shadow points inward. The square
     version cast an outward shadow because it was the thing being lifted; here the
     knob is. */
  box-shadow: inset 0 1px 2px rgba(15, 20, 30, 0.12);
  transition: background 0.25s ease, box-shadow 0.25s ease;

  /* ::before is the resting rim, ::after the brighter one that fades in on hover.
     Two layers rather than one, because CSS cannot interpolate between two conic
     gradients — animating a single rim would snap instead of fade. ::after is also
     rotated 30°, so the crossfade reads as the highlight travelling round the edge. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1.5px;
    pointer-events: none;
    /* Punches the middle out of the layer, leaving only a 1.5px ring. */
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    transition: opacity 0.2s ease;
  }

  /* The page behind this is pure #fff, so a broad white arc would simply vanish and
     leave the outline looking chipped. A specular highlight reads by local contrast
     with the edge beside it, not against the page — so the light rim is a continuous
     hairline broken by two narrow glints at upper-left and lower-right. */
  &::before {
    background: conic-gradient(
      rgba(10, 18, 34, 0.3) 0deg,
      rgba(10, 18, 34, 0.3) 110deg,
      rgba(255, 255, 255, 0.95) 135deg,
      rgba(10, 18, 34, 0.3) 160deg,
      rgba(10, 18, 34, 0.3) 290deg,
      rgba(255, 255, 255, 0.95) 315deg,
      rgba(10, 18, 34, 0.3) 340deg,
      rgba(10, 18, 34, 0.3) 360deg
    );
  }

  &::after {
    background: conic-gradient(
      rgba(10, 18, 34, 0.38) 0deg,
      rgba(10, 18, 34, 0.38) 140deg,
      rgba(255, 255, 255, 1) 165deg,
      rgba(10, 18, 34, 0.38) 190deg,
      rgba(10, 18, 34, 0.38) 320deg,
      rgba(255, 255, 255, 1) 345deg,
      rgba(10, 18, 34, 0.38) 360deg
    );
    opacity: 0;
  }

  /* macOS toolbar controls brighten in place on hover, they do not lift, so the rim
     and the track do the work and the geometry stays put. */
  &:hover {
    background: rgba(10, 18, 34, 0.12);

    &::after {
      opacity: 1;
    }

    .theme-toggle-knob {
      box-shadow:
        0 2px 5px rgba(15, 20, 30, 0.26),
        0 1px 1px rgba(15, 20, 30, 0.14);
    }
  }

  /* The press reads on the knob rather than the whole control — pressing a switch
     pushes the thing that moves, not the groove it moves in. */
  &:active .theme-toggle-knob {
    box-shadow: 0 1px 2px rgba(15, 20, 30, 0.28);
  }

  &:focus-visible {
    outline: 2px solid rgba(0, 39, 77, 0.5);
    outline-offset: 2px;
  }
}

.theme-toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  box-shadow:
    0 1px 3px rgba(15, 20, 30, 0.22),
    0 1px 1px rgba(15, 20, 30, 0.12);
  /* transform rather than `left`, so the travel runs on the compositor. The easing
     overshoots very slightly past 1, which is what gives a switch its mechanical snap. */
  transform: translateX(0);
  transition:
    transform 0.28s cubic-bezier(0.34, 1.28, 0.5, 1),
    background 0.25s ease,
    box-shadow 0.2s ease;
}

.theme-toggle-icon {
  width: 15px;
  height: 15px;
  object-fit: contain;
  /* One filter for both themes now. The knob is light whichever theme is on, so the
     glyph is always dark — the square version needed a per-theme filter because the
     glyph sat directly on a surface that inverted. */
  filter: brightness(0) invert(16%);
}

/* On #111318 the fill has to sit above its backdrop rather than below it, and the rim
   drops its dark arcs entirely — a glass edge in a dark room only ever catches light.
   Nothing vanishes into the page here, so this one stays a smooth sweep round the whole
   edge rather than the light theme's hairline-plus-glints. */
.theme-toggle.theme-toggle--dark {
  /* Straight from the reference's own track: bg-white/10. */
  background: rgba(255, 255, 255, 0.1);
  color: #e4e5eb;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.35);

  &::before {
    background: conic-gradient(
      from 135deg,
      rgba(255, 255, 255, 0.7) 0deg,
      rgba(255, 255, 255, 0.08) 90deg,
      rgba(255, 255, 255, 0.6) 180deg,
      rgba(255, 255, 255, 0.1) 270deg,
      rgba(255, 255, 255, 0.7) 360deg
    );
  }

  &::after {
    background: conic-gradient(
      from 185deg,
      rgba(255, 255, 255, 0.9) 0deg,
      rgba(255, 255, 255, 0.14) 90deg,
      rgba(255, 255, 255, 0.8) 180deg,
      rgba(255, 255, 255, 0.16) 270deg,
      rgba(255, 255, 255, 0.9) 360deg
    );
  }

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  &:focus-visible {
    outline-color: rgba(255, 255, 255, 0.5);
  }

  /* The knob travels to the far end and dims a shade — pure white against #111318 is
     brighter than anything else on the page. 56 − 26 − (3 × 2) = 24px of travel. */
  .theme-toggle-knob {
    transform: translateX(24px);
    background: #e8eaee;
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.5),
      0 1px 2px rgba(0, 0, 0, 0.35);
  }

  /* These need the extra pseudo-class to outrank the rule above: the light theme's
     hover/press shadows are only 3 classes deep, so the dark base would otherwise win
     on source order and the knob would give no feedback at all in dark mode. */
  &:hover .theme-toggle-knob {
    box-shadow:
      0 3px 9px rgba(0, 0, 0, 0.58),
      0 1px 2px rgba(0, 0, 0, 0.4);
  }

  &:active .theme-toggle-knob {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
  }
}

/* Without mask compositing the ring pseudo-elements would render as filled rectangles
   covering the whole control, so drop them and fall back to a hairline. inset box-shadow
   rather than `border`, so the fallback costs no layout. */
@supports not ((-webkit-mask-composite: xor) or (mask-composite: exclude)) {
  .theme-toggle::before,
  .theme-toggle::after {
    display: none;
  }

  .theme-toggle {
    box-shadow:
      inset 0 0 0 1px rgba(10, 18, 34, 0.16),
      inset 0 1px 2px rgba(15, 20, 30, 0.12);
  }

  .theme-toggle.theme-toggle--dark {
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.16),
      inset 0 1px 2px rgba(0, 0, 0, 0.35);
  }
}

/* The knob still has to move — position is the only thing telling you which theme is
   on — so only the travel is cut, not the state change. */
@media (prefers-reduced-motion: reduce) {
  .theme-toggle,
  .theme-toggle::before,
  .theme-toggle::after,
  .theme-toggle-knob {
    transition: none;
  }
}
</style>
